import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewBuilderResale } from "../../Redux/Actions/builderResaleAction";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewBuilder.css";

const ViewBuilderResale = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [builderData, setBuilderData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(viewBuilderResale({ builder_resale_id: id }))
      .then((res) => {
        if (res && res.statusCode === 200) {
          setBuilderData(res.data);
        } else {
          toast.error(res?.message || "Failed to load builder details");
        }
      })
      .catch(() => {
        toast.error("Error fetching builder details");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, id]);

  if (loading) {
    return (
      <MainLayout>
        <div className="vbr-loader-wrap">
          <div className="vbr-loader" />
          <p className="vbr-loader-text">Loading builder data…</p>
        </div>
      </MainLayout>
    );
  }

  if (!builderData) {
    return (
      <MainLayout>
        <div className="vbr-empty-wrap">
          <div className="vbr-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <p className="vbr-empty-text">No Builder Data Found.</p>
          <Link to="/builder_resale" className="vbr-back-link">← Back to Builders</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vbr-wrapper">

        <div className="vbr-header">
          <div className="vbr-header-left">
            <Link to="/builder_resale" className="vbr-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vbr-header-divider" />
            <div>
              <div className="vbr-breadcrumb">Builder Resale / List / View</div>
              <h1 className="vbr-title">{builderData?.brand_name || "No Data"}</h1>
            </div>
          </div>
          <Link to={`/builder_resale/edit/${id}`} className="vbr-edit-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit Builder
          </Link>
        </div>

        <div className="vbr-hero-card">
          <div className="vbr-hero-left">
            {builderData?.brand_logo ? (
              <div className="vbr-hero-logo-wrap">
                <img
                  src={builderData.brand_logo}
                  alt={builderData.brand_name || "Brand logo"}
                  className="vbr-hero-logo"
                />
              </div>
            ) : (
              <div className="vbr-hero-avatar">
                {builderData?.brand_name?.charAt(0)?.toUpperCase() || "B"}
              </div>
            )}
            <div>
              <span className="vbr-hero-label">Builder Name</span>
              <span className="vbr-hero-name">{builderData?.brand_name || "—"}</span>
              {builderData?.organization_name && (
                <span className="vbr-hero-brand">{builderData.organization_name}</span>
              )}
            </div>
          </div>
          <div className="vbr-hero-meta">
            {(builderData?.city?.city_name || builderData?.state?.state_name || builderData?.country?.country_name) && (
              <div className="vbr-hero-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {[builderData?.city?.city_name, builderData?.state?.state_name, builderData?.country?.country_name]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
          </div>
        </div>

        <div className="vbr-grid-layout">

          <div className="vbr-col">
            <div className="vbr-card">
              <div className="vbr-card-header">
                <span className="vbr-card-badge">01</span>
                <h2 className="vbr-card-title">Basic Information</h2>
              </div>
              <div className="vbr-info-grid">
                <div className="vbr-info-item">
                  <span className="vbr-info-label">Builder Name</span>
                  <span className="vbr-info-value">{builderData?.brand_name || "No Data"}</span>
                </div>
                <div className="vbr-info-item">
                  <span className="vbr-info-label">Organization Name</span>
                  <span className="vbr-info-value">{builderData?.organization_name || "No Data"}</span>
                </div>
                <div className="vbr-info-item vbr-info-item--full">
                  <span className="vbr-info-label">Brand Logo</span>
                  {builderData?.brand_logo ? (
                    <div className="vbr-logo-preview">
                      <img
                        src={builderData.brand_logo}
                        alt="Brand logo"
                        className="vbr-logo-preview-img"
                      />
                    </div>
                  ) : (
                    <span className="vbr-info-value">No Data</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="vbr-col">
            <div className="vbr-card">
              <div className="vbr-card-header">
                <span className="vbr-card-badge">02</span>
                <h2 className="vbr-card-title">Address Details</h2>
              </div>
              <div className="vbr-info-grid">
                <div className="vbr-info-item vbr-info-item--full">
                  <span className="vbr-info-label">Street Address</span>
                  <span className="vbr-info-value">{builderData?.street_address || "No Data"}</span>
                </div>
                <div className="vbr-info-item">
                  <span className="vbr-info-label">Country</span>
                  <span className="vbr-info-value">{builderData?.country?.country_name || "No Data"}</span>
                </div>
                <div className="vbr-info-item">
                  <span className="vbr-info-label">State</span>
                  <span className="vbr-info-value">{builderData?.state?.state_name || "No Data"}</span>
                </div>
                <div className="vbr-info-item">
                  <span className="vbr-info-label">City</span>
                  <span className="vbr-info-value">{builderData?.city?.city_name || "No Data"}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </MainLayout>
  );
};

export default ViewBuilderResale;